import { useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Dropdown, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useModal } from 'context/ModalContext';
import { getButtonsCancelOk } from 'context/helpers/helperModalContext';
import EToolTip from '../others/EToolTip';
import {
    buildDialogDefaultMessage,
    buildMessengerShareUrl,
    buildShareMessage,
    type MessengerKind,
} from './buildMessengerUrl';
import SolarIcon from 'components/icons/SolarIcon';

interface DlgButton {
    title: ReactNode;
    variant?: string;
    disabled?: boolean;
    handleClick?: () => void;
}

export interface ShareDocumentProps {
    /** Short message text (optional). */
    text?: string;
    /** Document / file public URL (optional). */
    link?: string;
    /** Open edit dialog before sending. */
    inputMessageDlg?: boolean;
    disabled?: boolean;
    className?: string;
    /** Override toggle icon. */
    icon?: ReactNode;
}

const MESSENGERS: Array<{ kind: MessengerKind; label: string; icon: string }> = [
    { kind: 'viber', label: 'Viber', icon: '/imgs/social/viber.png' },
    { kind: 'telegram', label: 'Telegram', icon: '/imgs/social/telegram.png' },
    { kind: 'whatsapp', label: 'WhatsApp', icon: '/imgs/social/whatsapp.png' },
];

const openMessengerUrl = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
};

const ShareDocument = ({
    text = '',
    link = '',
    inputMessageDlg = false,
    disabled = false,
    className = '',
    icon,
}: ShareDocumentProps) => {
    const { t } = useTranslation(['common']);
    const { showDlg } = useModal();
    const uid = useId();
    const refMessage = useRef<HTMLTextAreaElement | null>(null);

    const hasLink = Boolean(String(link || '').trim());
    const isDisabled = disabled || !hasLink;

    const sendMessage = (kind: MessengerKind, message: string) => {
        openMessengerUrl(buildMessengerShareUrl(kind, message, { link }));
    };

    const openEditDialog = (kind: MessengerKind) => {
        const messengerLabel =
            MESSENGERS.find((item) => item.kind === kind)?.label || kind;
        const messageId = `share-document-message-${uid}`;

        const buttons: DlgButton[] = getButtonsCancelOk(t).map((button) => ({
            ...button,
            handleClick: undefined,
        }));
        buttons[0].handleClick = () => showDlg(false);
        buttons[1].title = t('shareDocument.send');
        buttons[1].handleClick = () => {
            const message = String(
                refMessage.current?.value ||
                    (document.getElementById(messageId) as HTMLTextAreaElement | null)?.value ||
                    '',
            );
            showDlg(false);
            sendMessage(kind, message);
        };

        showDlg({
            type: 'confirm',
            title: t('shareDocument.dialogTitle', { messenger: messengerLabel }),
            buttons,
            idFirstFocus: messageId,
            text: (
                <Form.Group className="mb-0">
                    <Form.Label>{t('shareDocument.messageLabel')}</Form.Label>
                    <Form.Control
                        id={messageId}
                        ref={refMessage}
                        as="textarea"
                        rows={6}
                        defaultValue={buildDialogDefaultMessage(text, link)}
                        placeholder={t('shareDocument.messagePlaceholder')}
                        style={{ resize: 'vertical' }}
                    />
                </Form.Group>
            ),
        });
    };

    const handleSelect = (kind: MessengerKind) => {
        if (isDisabled) return;
        if (inputMessageDlg) {
            openEditDialog(kind);
            return;
        }
        sendMessage(kind, buildShareMessage(text, link));
    };

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm share-document-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {MESSENGERS.map((item) => (
                <Dropdown.Item
                    key={item.kind}
                    as="button"
                    onClick={(e) => {
                        handleSelect(item.kind);
                        e.currentTarget.blur();
                    }}
                >
                    <img className="img-social me-2" src={item.icon} alt="" />
                    {item.label}
                </Dropdown.Item>
            ))}
        </Dropdown.Menu>
    );

    return (
        <EToolTip title={t('shareDocument.title')}>
            <span className={`d-inline-flex share-document-wrap ${className}`.trim()}>
                <Dropdown align="end" className="dd-share share-document-dd">
                    <Dropdown.Toggle
                        size="sm"
                        variant="light"
                        className="share-document-toggle"
                        id={`share-document-${uid}`}
                        disabled={isDisabled}
                        aria-label={t('shareDocument.title')}
                    >
                        {icon ?? <SolarIcon name="share" />}
                    </Dropdown.Toggle>

                    {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
                </Dropdown>
            </span>
        </EToolTip>
    );
};

export default ShareDocument;
